import { useState, useEffect } from 'react';
import { loginWithPassport, getPassportUserInfo } from '../config/passport';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import api from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';

/**
 * PassportConnectButton Component
 *
 * This component provides a button for users to connect with Immutable Passport.
 * It handles the login process and updates the authentication context.
 */
export default function PassportConnectButton({ className = '' }) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const { updateUserContext, user, isPassportUser } = useAuth();

  // Check if already connected with Passport
  useEffect(() => {
    // More robust check for Passport connection
    const checkPassportConnection = () => {
      // Check user context
      const isConnectedViaContext = user?.isConnected && isPassportUser();

      // Also check localStorage directly as a fallback
      const walletProvider = localStorage.getItem('wallet_provider');
      const passportAddress = localStorage.getItem('passport_address');
      const passportUserInfo = localStorage.getItem('passport_user_info');
      const isConnectedViaStorage = walletProvider === 'passport' && passportAddress && passportUserInfo;

      // Set connected if either check passes
      if (isConnectedViaContext || isConnectedViaStorage) {
        setIsConnected(true);
        console.log('PassportConnectButton: Detected active Passport connection');
      } else {
        setIsConnected(false);
        console.log('PassportConnectButton: No active Passport connection detected');
      }
    };

    // Run the check immediately
    checkPassportConnection();

    // Set up a listener for storage events from other tabs or the callback page
    const handleStorageChange = (event) => {
      if (event.key === 'passport_address' || event.key === 'passport_user_info' ||
          event.key === 'wallet_provider' || event.key === 'user') {
        checkPassportConnection();
      }
    };

    // Set up listeners for custom events from the PassportCallback component
    const handlePassportAuthenticated = () => {
      console.log('PassportConnectButton: Received passport-authenticated event');
      setIsConnected(true);
    };

    const handlePassportUserUpdated = () => {
      console.log('PassportConnectButton: Received passport-user-updated event');
      setIsConnected(true);
    };

    // Add event listeners
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('passport-authenticated', handlePassportAuthenticated);
    window.addEventListener('passport-user-updated', handlePassportUserUpdated);

    // Clean up event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('passport-authenticated', handlePassportAuthenticated);
      window.removeEventListener('passport-user-updated', handlePassportUserUpdated);
    };
  }, [user, isPassportUser]);

  // If already connected with Passport, don't show the button
  if (isConnected) {
    return null;
  }

  const handleConnect = async () => {
    if (isConnecting) return;

    setIsConnecting(true);
    const loadingToastId = toast.loading('Connecting to Immutable Passport...');

    try {
      console.log('Starting Passport login process...');

      // Log the Passport instance configuration
      console.log('Passport configuration:', {
        clientId: import.meta.env.VITE_IMMUTABLE_CLIENT_ID || 'Not set in env',
        redirectUri: 'https://app.bitsport.gg/passport-callback',
        logoutRedirectUri: 'https://app.bitsport.gg/passport-logout'
      });

      const { provider, accounts, userInfo } = await loginWithPassport();
      console.log('Login successful, provider:', provider);
      console.log('Accounts:', accounts);

      if (accounts && accounts.length > 0) {
        const address = accounts[0];
        console.log('Connected with address:', address);

        // Create a timestamp for the session
        const timestamp = Date.now();
        const sessionExpiry = timestamp + (14 * 24 * 60 * 60 * 1000); // 14 days in milliseconds

        // Create base64 token string for API authentication
        const messageToSign = `Authenticate with BitPool for address ${address} at timestamp ${timestamp}`;
        const base64Token = btoa(JSON.stringify({
          address,
          timestamp,
          message: messageToSign,
          provider: 'passport',
          expiry: sessionExpiry
        }));

        // Store token in localStorage
        localStorage.setItem('token', base64Token);

        // Create basic user object
        const basicUser = {
          address,
          isConnected: true,
          provider: 'passport',
          passportInfo: userInfo,
          passportAddress: address,
          timestamp,
          sessionExpiry,
          token: base64Token
        };

        // Store wallet connection info for reconnection
        localStorage.setItem('wallet_connected', 'true');
        localStorage.setItem('wallet_address', address);
        localStorage.setItem('wallet_provider', 'passport');
        localStorage.setItem('wallet_last_connected', timestamp.toString());
        localStorage.setItem('passport_address', address);
        if (userInfo) {
          localStorage.setItem('passport_user_info', JSON.stringify(userInfo));
        }
        localStorage.setItem('user', JSON.stringify(basicUser));
        console.log('Connection info stored in localStorage');

        // Update the auth context with the new user
        updateUserContext(basicUser);
        console.log('User context updated with:', basicUser);

        // Mark as connected locally to hide button immediately
        setIsConnected(true);

        // Get user info from API
        try {
          const userApiInfo = await api.get(API_ENDPOINTS.wallet.get(address));
          const fullUser = {
            ...basicUser,
            isAdmin: userApiInfo.isAdmin || false,
            username: userApiInfo.username,
            bitpBalance: userApiInfo.bitpBalance,
            usdBalance: userApiInfo.usdBalance,
            token: base64Token
          };

          // Update localStorage and context with full user info
          localStorage.setItem('user', JSON.stringify(fullUser));
          updateUserContext(fullUser);
          console.log('User profile retrieved successfully');
        } catch (apiError) {
          console.error('Error getting user profile from API:', apiError);
          // Continue with basic user info
        }

        // Show success message
        toast.success('Successfully connected with Immutable Passport', { id: loadingToastId });
      } else {
        console.error('No accounts returned from Passport login');
        toast.error('Failed to get wallet address from Immutable Passport', { id: loadingToastId });
      }
    } catch (error) {
      console.error('Error connecting with Passport:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.stack) {
        console.error('Error stack:', error.stack);
      }

      // Show a user-friendly error message
      if (error.isNetworkError) {
        toast.error('Network error: Please make sure you are connected to the correct network.', { id: loadingToastId });
      } else {
        toast.error(`Failed to connect with Immutable Passport: ${error.message || 'Please try again.'}`, { id: loadingToastId });
      }

      // Clear any passport-related data to prevent conflicts with AppKit
      localStorage.removeItem('wallet_provider');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <button
      onClick={handleConnect}
      disabled={isConnecting}
      className={`flex items-center justify-center px-4 py-2 rounded-lg bg-[#040F20] hover:bg-[#0A1A33] text-white font-medium transition-colors ${isConnecting ? 'opacity-70 cursor-not-allowed' : ''} ${className}`}
    >
      {isConnecting ? (
        <>
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
          Connecting...
        </>
      ) : (
        <>
          <img
            src="/imx.svg"
            alt="Immutable"
            className="w-5 h-5 mr-2"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
            }}
          />
          Sign in with Immutable
        </>
      )}
    </button>
  );
}
